import type { BBox } from '@turf/turf';

export interface MapboxFeature {
  bbox?: BBox;
  center: [number, number];
  place_name: string;
}

export interface MapboxGeocodingResponse {
  features: MapboxFeature[];
}

export interface FetchCountryBboxOptions {
  country: string;
  mapboxToken: string;
  signal?: AbortSignal;
}

/**
 * Fetches the bounding box for a country using Mapbox Geocoding API
 * @param options - Options object
 * @param options.country - Country name or code
 * @param options.mapboxToken - Mapbox access token
 * @param options.signal - Optional AbortSignal for cancellation
 * @returns Bounding box [minLng, minLat, maxLng, maxLat] or null if not found
 */
export async function fetchCountryBbox(
  options: FetchCountryBboxOptions
): Promise<BBox | null> {
  const { country, mapboxToken, signal } = options;

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(country)}.json?types=country&access_token=${mapboxToken}`,
      { signal }
    );

    if (!response.ok) {
      console.error('Mapbox API error:', response.status, response.statusText);
      return null;
    }

    const data: MapboxGeocodingResponse = await response.json();
    const bbox = data?.features?.[0]?.bbox;

    if (bbox && bbox.length === 4) {
      return bbox as BBox;
    }

    return null;
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      console.error('Failed to fetch country bbox:', error);
    }

    return null;
  }
}

export interface FetchCountryBboxFromCoordsOptions {
  lng: number;
  lat: number;
  mapboxToken: string;
  signal?: AbortSignal;
}

/**
 * Fetches the country bounding box from coordinates using Mapbox Reverse Geocoding API
 * @param options - Options object
 * @param options.lng - Longitude
 * @param options.lat - Latitude
 * @param options.mapboxToken - Mapbox access token
 * @param options.signal - Optional AbortSignal for cancellation
 * @returns Bounding box [minLng, minLat, maxLng, maxLat] or null if not found
 */
export async function fetchCountryBboxFromCoords(
  options: FetchCountryBboxFromCoordsOptions
): Promise<BBox | null> {
  const { lng, lat, mapboxToken, signal } = options;

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=country&access_token=${mapboxToken}`,
      { signal }
    );

    if (!response.ok) {
      console.error('Mapbox API error:', response.status, response.statusText);
      return null;
    }

    const data: MapboxGeocodingResponse = await response.json();
    const bbox = data?.features?.[0]?.bbox;

    if (bbox && bbox.length === 4) {
      return bbox as BBox;
    }

    return null;
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      console.error('Failed to fetch country bbox from coords:', error);
    }

    return null;
  }
}
